/**
 * Admin Returns — final-approval queue for clerk-verified returns.
 *
 * Backend contract (unchanged):
 *   GET /api/returns                      → clerk_verified / approved / rejected for admins
 *   PUT /api/returns/{id}/status          → approve or reject
 *
 * Tabs:
 *   • Awaiting (clerk_verified) — cards with student / patient / items / photos + approve/reject
 *   • Approved / Rejected — read-only history
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, XCircle, Image as ImageIcon, X } from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { shortRef } from '../ui';

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function ReturnsPage({ ctx }) {
  const { showToast } = ctx;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('awaiting'); // awaiting | approved | rejected
  const [photoViewer, setPhotoViewer] = useState(null); // url string
  const [decision, setDecision] = useState(null); // { ret, action: 'approve'|'reject', reason }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(endpoints.returns.list);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('returns', e);
      showToast && showToast('Failed to load returns', 'err');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const bucket = useMemo(() => {
    return {
      awaiting: rows.filter((r) => r.status === 'clerk_verified'),
      approved: rows.filter((r) => r.status === 'approved'),
      rejected: rows.filter((r) => r.status === 'rejected'),
    };
  }, [rows]);

  const openDecision = (ret, action) => setDecision({ ret, action, reason: '' });

  const submitDecision = async () => {
    if (!decision) return;
    const { ret, action, reason } = decision;
    if (action === 'reject' && !reason.trim()) {
      showToast && showToast('A reason is required to reject', 'err');
      return;
    }
    setSaving(true);
    try {
      await apiClient.put(endpoints.returns.statusUpdate(ret.id), {
        status: action === 'approve' ? 'approved' : 'rejected',
        reason: reason.trim() || undefined,
      });
      showToast && showToast(`Return ${action === 'approve' ? 'approved' : 'rejected'}`, 'ok');
      setDecision(null);
      await load();
    } catch (e) {
      showToast && showToast(e?.response?.data?.detail || 'Update failed', 'err');
    } finally {
      setSaving(false);
    }
  };

  const currentList = bucket[tab] || [];

  return (
    <div data-testid="ap-returns-page">
      <div className="ap-eyebrow">Faculty Console</div>
      <h1 className="ap-page-title">Returns</h1>
      <p className="ap-page-lede">
        Final approval queue for returns that a clerk has already verified with photos.
        Approving credits units into the sterilisation-gated Re-issuable pool.
      </p>

      <div className="ap-ind-row" role="group" aria-label="Returns summary">
        <div className="ap-ind" data-testid="ap-returns-count-awaiting">
          <div className="ap-ind-label"><em className="is-exc" />Awaiting approval</div>
          <div className="ap-ind-value">{bucket.awaiting.length}</div>
          <div className="ap-ind-hint">Clerk-verified, needs your call</div>
        </div>
        <div className="ap-ind" data-testid="ap-returns-count-approved">
          <div className="ap-ind-label">Approved</div>
          <div className="ap-ind-value">{bucket.approved.length}</div>
          <div className="ap-ind-hint">Sent to Re-issuable pool</div>
        </div>
        <div className="ap-ind" data-testid="ap-returns-count-rejected">
          <div className="ap-ind-label"><em className="is-warn" />Rejected</div>
          <div className="ap-ind-value">{bucket.rejected.length}</div>
          <div className="ap-ind-hint">Sent back to student</div>
        </div>
      </div>

      <div className="ap-mt-6" style={{ display: 'flex', gap: 8, borderBottom: '1px solid var(--diis-hairline)' }}>
        {[
          { id: 'awaiting', label: `Awaiting (${bucket.awaiting.length})` },
          { id: 'approved', label: `Approved (${bucket.approved.length})` },
          { id: 'rejected', label: `Rejected (${bucket.rejected.length})` },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`ap-returns-tab-${t.id}`}
            style={{
              padding: '10px 14px',
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              fontSize: 13,
              letterSpacing: '0.02em',
              color: tab === t.id ? 'var(--diis-ink)' : 'var(--diis-ink-subtle)',
              borderBottom: tab === t.id ? '2px solid var(--diis-accent)' : '2px solid transparent',
              marginBottom: -1,
              fontWeight: tab === t.id ? 600 : 400,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="ap-mt-6" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {loading ? (
          <div className="ap-empty">Loading returns…</div>
        ) : currentList.length === 0 ? (
          <div className="ap-empty" data-testid={`ap-returns-empty-${tab}`}>
            {tab === 'awaiting' ? 'No returns awaiting your approval.' : `No ${tab} returns.`}
          </div>
        ) : (
          currentList.map((r) => (
            <ReturnCard
              key={r.id}
              ret={r}
              tab={tab}
              onApprove={() => openDecision(r, 'approve')}
              onReject={() => openDecision(r, 'reject')}
              onViewPhoto={setPhotoViewer}
            />
          ))
        )}
      </div>

      {photoViewer && (
        <div className="ap-modal-scrim" onClick={() => setPhotoViewer(null)} data-testid="ap-returns-photo-modal">
          <div className="ap-modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div className="ap-modal-header">
              <h3 style={{ margin: 0, fontFamily: 'var(--diis-font-display)', fontWeight: 500, fontSize: 18 }}>Photo</h3>
              <button type="button" className="ap-btn ap-btn-ghost ap-btn-sm" onClick={() => setPhotoViewer(null)} aria-label="Close"><X size={14} /></button>
            </div>
            <div className="ap-modal-body" style={{ padding: 0 }}>
              <img src={photoViewer} alt="verification" style={{ width: '100%', display: 'block' }} />
            </div>
          </div>
        </div>
      )}

      {decision && (
        <div className="ap-modal-scrim" onClick={() => !saving && setDecision(null)} data-testid="ap-returns-decision-modal">
          <div className="ap-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="ap-modal-header">
              <div>
                <h3 style={{ margin: 0, fontFamily: 'var(--diis-font-display)', fontWeight: 500, fontSize: 20 }}>
                  {decision.action === 'approve' ? 'Approve return?' : 'Reject return?'}
                </h3>
                <p className="ap-subtle" style={{ margin: '4px 0 0', fontSize: 12 }}>
                  {decision.ret.student_name} · {shortRef(decision.ret.id, 'RET')} · {(decision.ret.items || []).length} item(s)
                </p>
              </div>
              <button type="button" className="ap-btn ap-btn-ghost ap-btn-sm" onClick={() => !saving && setDecision(null)} aria-label="Close"><X size={14} /></button>
            </div>
            <div className="ap-modal-body">
              <label>
                <div className="ap-field-label">
                  {decision.action === 'approve' ? 'Notes (optional)' : 'Reason (required)'}
                </div>
                <textarea
                  className="ap-textarea"
                  rows={3}
                  value={decision.reason}
                  onChange={(e) => setDecision({ ...decision, reason: e.target.value })}
                  data-testid="ap-returns-decision-reason"
                />
              </label>
            </div>
            <div className="ap-modal-footer">
              <button type="button" className="ap-btn ap-btn-ghost" onClick={() => !saving && setDecision(null)}>Cancel</button>
              <button
                type="button"
                className="ap-btn ap-btn-primary"
                onClick={submitDecision}
                disabled={saving || (decision.action === 'reject' && !decision.reason.trim())}
                data-testid="ap-returns-decision-submit"
              >
                {saving ? 'Saving…' : decision.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReturnCard({ ret, tab, onApprove, onReject, onViewPhoto }) {
  const items = ret.items || [];
  const studentPhotos = ret.return_photos || [];
  const clerkPhotos = ret.clerk_verification_photos || [];

  return (
    <div
      data-testid={`ap-returns-card-${ret.id}`}
      style={{
        border: '1px solid var(--diis-hairline)',
        borderRadius: 'var(--diis-radius-lg)',
        background: 'var(--diis-surface)',
        padding: 20,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20 }}>
        <div>
          <div style={{ fontWeight: 500, color: 'var(--diis-ink)', fontSize: 15 }}>
            {ret.student_name || '—'}
            <span className="ap-subtle" style={{ fontSize: 12, marginLeft: 8, letterSpacing: '0.02em' }}>
              {shortRef(ret.id, 'RET')} · {ret.student_category || ''}
            </span>
          </div>
          <div className="ap-subtle" style={{ fontSize: 12, marginTop: 4 }}>
            Submitted {fmt(ret.created_at)} · Clerk-verified {fmt(ret.clerk_verified_at)}
          </div>
        </div>
        {tab === 'awaiting' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="ap-btn ap-btn-ghost" onClick={onReject} data-testid={`ap-returns-reject-${ret.id}`}>
              <XCircle size={14} style={{ marginRight: 4 }} /> Reject
            </button>
            <button type="button" className="ap-btn ap-btn-primary" onClick={onApprove} data-testid={`ap-returns-approve-${ret.id}`}>
              <CheckCircle2 size={14} style={{ marginRight: 4 }} /> Approve
            </button>
          </div>
        )}
        {tab !== 'awaiting' && (
          <span style={{
            fontSize: 11,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: tab === 'approved' ? '#0F766E' : '#B04A3C',
          }}>
            {tab}
            {ret.admin_processed_at && ` · ${fmt(ret.admin_processed_at)}`}
          </span>
        )}
      </div>

      <div style={{ marginTop: 16, borderTop: '1px solid var(--diis-hairline)', paddingTop: 12 }}>
        <div className="ap-field-label" style={{ marginBottom: 8 }}>Items ({items.length})</div>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, borderBottom: i < items.length - 1 ? '1px dashed var(--diis-hairline)' : 0 }}>
            <span>
              <strong>{it.component_name}</strong>
              <span className="ap-subtle" style={{ marginLeft: 6 }}>
                {it.brand_name || '—'}{it.size ? ` · ${it.size}` : ''}
              </span>
            </span>
            <span className="cp-mono-num">× {it.quantity}</span>
          </div>
        ))}
      </div>

      {ret.reason && (
        <div style={{ marginTop: 12, fontSize: 13 }}>
          <span className="ap-field-label" style={{ marginRight: 6 }}>{tab === 'awaiting' ? 'Student note:' : 'Decision reason:'}</span>
          {ret.reason}
        </div>
      )}

      {(studentPhotos.length > 0 || clerkPhotos.length > 0) && (
        <div style={{ marginTop: 16, borderTop: '1px solid var(--diis-hairline)', paddingTop: 12 }}>
          {studentPhotos.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div className="ap-field-label" style={{ marginBottom: 6 }}>Student return photos</div>
              <PhotoStrip photos={studentPhotos} onView={onViewPhoto} testidPrefix={`ap-returns-photo-student-${ret.id}`} />
            </div>
          )}
          {clerkPhotos.length > 0 && (
            <div>
              <div className="ap-field-label" style={{ marginBottom: 6 }}>Clerk verification photos</div>
              <PhotoStrip photos={clerkPhotos} onView={onViewPhoto} testidPrefix={`ap-returns-photo-clerk-${ret.id}`} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PhotoStrip({ photos, onView, testidPrefix }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {photos.map((p, i) => {
        const url = String(p);
        // Backend now returns absolute URLs served via the storage layer.
        return (
          <button
            key={i}
            type="button"
            onClick={() => onView(url)}
            data-testid={`${testidPrefix}-${i}`}
            style={{
              width: 72, height: 72,
              padding: 0, border: '1px solid var(--diis-hairline)',
              borderRadius: 6, cursor: 'pointer', background: 'var(--diis-canvas-alt)',
              overflow: 'hidden',
            }}
            aria-label={`Open photo ${i + 1}`}
          >
            <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <ImageIcon size={16} style={{ opacity: 0.4 }} />
          </button>
        );
      })}
    </div>
  );
}
